/**
 * Token utilities for player self-scoring
 * Generates unique, secure tokens for match scoring links
 */

/**
 * Generates a unique token for a player's match scoring link
 * Uses crypto.randomUUID() for secure random generation
 *
 * @returns {string} A unique token string
 */
export function generateMatchToken() {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

/**
 * Generates tokens for both players in a match
 *
 * @param {string} tournamentId - The tournament ID
 * @param {string|number} matchId - The match ID
 * @returns {{ p1Token: string, p2Token: string, tokenCreatedAt: string }}
 */
export function generateMatchTokens(tournamentId, matchId) {
  return {
    p1Token: generateMatchToken(),
    p2Token: generateMatchToken(),
    tokenCreatedAt: new Date().toISOString()
  };
}

/**
 * Builds the scoring URL for a player
 *
 * @param {string} token - The player's match token
 * @param {string} [tournamentId] - Tournament ID for direct lookup
 * @param {string} [baseUrl] - Optional base URL (defaults to window.location.origin)
 * @param {string} [instanceDate] - Occurrence date for recurring tournaments, so the
 *   scoring page can fetch that occurrence's subcollection document directly.
 * @returns {string} Full scoring URL
 */
export function buildScoringUrl(token, tournamentId, baseUrl, instanceDate) {
  const origin = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  // Include tournamentId in URL for efficient lookup: /score/{tournamentId}/{token}
  const path = tournamentId
    ? `${origin}/score/${tournamentId}/${token}`
    : `${origin}/score/${token}`;
  return instanceDate ? `${path}?i=${encodeURIComponent(instanceDate)}` : path;
}

/**
 * Validates a token format
 *
 * @param {string} token - Token to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateTokenFormat(token) {
  if (!token || typeof token !== 'string') {
    return { valid: false, error: 'Token is required' };
  }

  // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  if (!uuidRegex.test(token)) {
    return { valid: false, error: 'Invalid token format' };
  }

  return { valid: true };
}

/**
 * Checks if match tokens have expired
 * Tokens expire when the match is finished
 *
 * @param {object} match - Match object with token data
 * @returns {{ expired: boolean, reason?: string }}
 */
export function checkTokenExpiration(match) {
  if (!match) {
    return { expired: true, reason: 'Match not found' };
  }

  // Token expires if match is already finished
  if (match.finished) {
    return { expired: true, reason: 'Match has already been completed' };
  }

  // Token expires if match was cancelled
  if (match.cancelled) {
    return { expired: true, reason: 'Match was cancelled' };
  }

  return { expired: false };
}

/**
 * Finds a match by token in a bracket
 *
 * @param {Array} bracket - Tournament bracket array
 * @param {string} token - Token to search for
 * @returns {{ match: object|null, playerPosition: 'p1'|'p2'|null, matchIndex: number|null }}
 */
export function findMatchByToken(bracket, token) {
  if (!bracket || !Array.isArray(bracket) || !token) {
    return { match: null, playerPosition: null, matchIndex: null };
  }

  for (let i = 0; i < bracket.length; i++) {
    const match = bracket[i];

    if (match.p1Token === token) {
      return { match, playerPosition: 'p1', matchIndex: i };
    }

    if (match.p2Token === token) {
      return { match, playerPosition: 'p2', matchIndex: i };
    }
  }

  return { match: null, playerPosition: null, matchIndex: null };
}

/**
 * Determines if a match is ready for player self-scoring
 * A match is ready when both players are assigned and it hasn't started yet
 *
 * @param {object} match - Match object
 * @returns {boolean}
 */
export function isMatchReadyForScoring(match) {
  if (!match) return false;

  // Both players must be assigned
  const hasPlayer1 = match.player1 && match.player1 !== 'BYE' && match.player1 !== 'TBD';
  const hasPlayer2 = match.player2 && match.player2 !== 'BYE' && match.player2 !== 'TBD';

  if (!hasPlayer1 || !hasPlayer2) return false;

  // Match must not be finished
  if (match.finished) return false;

  // Match must not be cancelled
  if (match.cancelled) return false;

  return true;
}

/**
 * Gets player info from a match based on player position
 *
 * @param {object} match - Match object
 * @param {'p1'|'p2'} position - Player position
 * @returns {{ name: string, phone?: string, isOpponent: boolean }|null}
 */
export function getPlayerInfo(match, position) {
  if (!match || !position) return null;

  const isP1 = position === 'p1';

  return {
    name: isP1 ? match.player1 : match.player2,
    phone: isP1 ? match.p1Phone : match.p2Phone,
    isOpponent: false
  };
}

/**
 * Gets opponent info from a match based on player position
 *
 * @param {object} match - Match object
 * @param {'p1'|'p2'} position - Current player's position
 * @returns {{ name: string, phone?: string, isOpponent: boolean }|null}
 */
export function getOpponentInfo(match, position) {
  if (!match || !position) return null;

  const isP1 = position === 'p1';

  return {
    name: isP1 ? match.player2 : match.player1,
    phone: isP1 ? match.p2Phone : match.p1Phone,
    isOpponent: true
  };
}
